import React, { FC, useState, useEffect, useRef, useCallback } from 'react';
import './TooltipOnRef.scss';
import ReactDOM from 'react-dom';

export enum ETooltipPosition {
    TOP = 'top',
    BOTTOM = 'bottom',
    LEFT = 'left',
    RIGHT = 'right',
}

interface ITooltipProps {
  children: React.ReactNode;
  targetRef: React.RefObject<HTMLElement>;
  position: ETooltipPosition;
}

const Tooltip: FC<ITooltipProps> = ({ children, targetRef, position }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({});
  const tooltipRef = useRef<HTMLDivElement>(null);

  const handleMouseOver = useCallback(() => setIsVisible(true), []);
  const handleMouseOut = useCallback(() => setIsVisible(false), []);

  useEffect(() => {
    const targetElement = targetRef.current;
    if (!targetElement) return;

    targetElement.addEventListener('mouseover', handleMouseOver);
    targetElement.addEventListener('mouseout', handleMouseOut);

    return () => {
      targetElement.removeEventListener('mouseover', handleMouseOver);
      targetElement.removeEventListener('mouseout', handleMouseOut);
    };
  }, [handleMouseOver, handleMouseOut, targetRef]);

  const positionTooltip = useCallback(() => {
    const targetElement = targetRef.current;
    const tooltipElement = tooltipRef.current;
    if (!targetElement || !tooltipElement || !isVisible) return;

    const targetRect = targetElement.getBoundingClientRect();
    const tooltipRect = tooltipElement.getBoundingClientRect();
    let top = 0, left = 0;
    const marginFromRef = 32;

    switch (position) {
      case ETooltipPosition.TOP:
        top = targetRect.top - tooltipRect.height - marginFromRef;
        left = targetRect.left + (targetRect.width / 2) - (tooltipRect.width / 2);
        break;
      case ETooltipPosition.BOTTOM:
        top = targetRect.bottom + marginFromRef;
        left = targetRect.left + (targetRect.width / 2) - (tooltipRect.width / 2);
        break;
      case ETooltipPosition.LEFT:
        top = targetRect.top + (targetRect.height / 2) - (tooltipRect.height / 2);
        left = targetRect.left - tooltipRect.width - marginFromRef;
        break;
      case ETooltipPosition.RIGHT:
        top = targetRect.top + (targetRect.height / 2) - (tooltipRect.height / 2);
        left = targetRect.right + marginFromRef;
        break;
    }

    setTooltipStyle({
      top: `${top + window.scrollY}px`,
      left: `${left + window.scrollX}px`,
      position: 'absolute',
      zIndex: 1000,
    });
  }, [targetRef, isVisible, position]);

  useEffect(() => {
    if (isVisible) positionTooltip();
  }, [positionTooltip, isVisible]);

  return isVisible ? ReactDOM.createPortal(
    <div className='tooltip-on-ref' ref={tooltipRef} data-position={position} style={tooltipStyle}>
      <div className="tooltip-content">{children}</div>
    </div>,
    document.getElementById("app")
  ) : null;
};

export default Tooltip;
